import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Send, KeyRound, FileSignature, Landmark, Copy, Eye, FileDown, Presentation } from 'lucide-react';
import EquityDemoMode from './EquityDemoMode';

const MODES = [
  { v: 'internal', l: 'Within Equity (IFT)' },
  { v: 'subsidiary', l: 'Equity subsidiary (cross-country)' },
  { v: 'rtgs', l: 'RTGS' },
  { v: 'pesalink_bank', l: 'PesaLink (bank)' },
  { v: 'pesalink_mobile', l: 'PesaLink (mobile)' },
  { v: 'swift', l: 'SWIFT (international)' },
];

const copyText = (t) => { navigator.clipboard.writeText(t); toast.success('Copied'); };

// Official Jenga UAT test resources (developer.jengahq.io/guides/testing/testing-resources)
const UAT_PRESETS = {
  internal: { source_account: '1100194977404', source_name: 'Bullaale AS', dest_name: 'Tom Doe', dest_account: '0020100014605', dest_bank_code: '', dest_mobile: '', dest_bank_bic: '', dest_address: '', dest_country: 'KE', currency: 'KES', source_currency: '', amount: '100' },
  subsidiary: { source_account: '1450160649886', source_name: 'John Doe', dest_name: 'Jane Doe', dest_account: '1036200681230', dest_bank_code: '', dest_mobile: '', dest_bank_bic: '', dest_address: '', dest_country: 'UG', currency: 'UGX', source_currency: 'KES', amount: '100' },
  rtgs: { source_account: '1450160649886', source_name: 'John Doe', dest_name: 'Herr Sergius', dest_account: '1100194977404', dest_bank_code: '61', dest_mobile: '', dest_bank_bic: '', dest_address: '', dest_country: 'KE', currency: 'KES', source_currency: 'KES', purpose_code: 'OTHR', amount: '200' },
  pesalink_bank: { source_account: '1450160649886', source_name: 'John Doe', dest_name: 'Tom Doe', dest_account: '99901288715910', dest_bank_code: '57', dest_mobile: '', dest_bank_bic: '', dest_address: '', dest_country: 'KE', currency: 'KES', source_currency: '', amount: '100' },
  pesalink_mobile: { source_account: '1450160649886', source_name: 'John Doe', dest_name: 'Tom Doe', dest_account: '', dest_bank_code: '01', dest_mobile: '0722000000', dest_bank_bic: '', dest_address: '', dest_country: 'KE', currency: 'KES', source_currency: '', amount: '40' },
  swift: { source_account: '0810178838044', source_name: 'John Doe', dest_name: 'Rtest', dest_account: '10291281982198', dest_bank_code: '', dest_mobile: '', dest_bank_bic: 'BRASITMMXXX', dest_address: '24rth Street Turin', dest_country: 'IT', currency: 'USD', source_currency: 'USD', amount: '200.98' },
};

const EquityLayer = () => {
  const [env, setEnv] = useState('sandbox');
  const [demoMode, setDemoMode] = useState(false);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sigFields, setSigFields] = useState('54236000005287100.00KESEZYEQTEST001');
  const [sigResult, setSigResult] = useState(null);
  const [tokenResult, setTokenResult] = useState(null);
  const [form, setForm] = useState({
    mode: 'internal', source_country: 'KE', source_name: '', source_account: '',
    dest_type: 'bank', dest_country: 'KE', dest_name: '', dest_account: '', dest_bank_code: '', dest_mobile: '', dest_bank_bic: '', dest_address: '', purpose_code: 'OTHR', source_currency: '',
    amount: '', currency: 'KES', reference: '', description: 'EzyTransfer settlement',
    source_rail: 'mint', source_rail_reference: '',
  });
  const [prepared, setPrepared] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [mintTxns, setMintTxns] = useState(null);
  const [mintLoading, setMintLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, s] = await Promise.allSettled([api.get('/equity/health'), api.get('/equity/settlements')]);
      if (h.status === 'fulfilled') setHealth(h.value.data);
      if (s.status === 'fulfilled') setSettlements(s.value.data.settlements || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setForm((f) => ({ ...f, currency: env === 'production' ? 'USD' : 'KES' })); setPrepared(null); }, [env]);

  const genSignature = async () => {
    setBusy(true);
    try {
      const r = await api.post('/equity/signature', { fields: [sigFields] });
      setSigResult(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Signature failed'); }
    finally { setBusy(false); }
  };

  const testToken = async () => {
    setBusy(true);
    setTokenResult(null);
    try {
      const r = await api.post(`/equity/token?env=${env}`);
      setTokenResult(r.data);
      toast.success(`Bearer token OK (${env})`);
    } catch (e) {
      setTokenResult({ ok: false, error: e.response?.data?.detail });
      toast.error(e.response?.data?.detail || 'Token failed');
    } finally { setBusy(false); }
  };

  const loadTestData = () => {
    setForm((f) => ({ ...f, ...(UAT_PRESETS[f.mode] || UAT_PRESETS.internal) }));
    setPrepared(null);
    toast.success('Official UAT test data loaded');
  };

  const loadProdConfig = () => {
    setForm((f) => ({
      ...f, mode: 'internal', currency: 'USD', source_country: 'KE', dest_country: 'KE',
      source_name: st?.source_name || 'Bullaale AS', source_account: st?.source_account || '',
      source_currency: 'USD', description: 'EzyTransfer USD settlement', source_rail: 'mint',
    }));
    setPrepared(null);
    toast.success('Production USD settlement config loaded');
  };

  const formValid = () => {
    if (!form.dest_name || !form.amount) return false;
    return form.mode === 'pesalink_mobile' ? !!form.dest_mobile : !!form.dest_account;
  };

  const withDemoDefaults = (f) => {
    const p = UAT_PRESETS[f.mode] || UAT_PRESETS.internal;
    const out = { ...f };
    ['source_account', 'source_name', 'dest_name', 'dest_account', 'dest_bank_code', 'dest_mobile', 'dest_bank_bic', 'dest_address', 'currency', 'amount'].forEach((k) => {
      if (!out[k] && p[k]) out[k] = p[k];
    });
    return out;
  };

  const txPayload = (f = form) => {
    const { _rail_details, ...rest } = f;
    return { ...rest, amount: parseFloat(f.amount), env, source_rail_details: _rail_details || null };
  };

  const prepare = async () => {
    let f = form;
    if (!formValid()) {
      f = withDemoDefaults(form);
      setForm(f);
      toast.info('Missing fields auto-filled with demo values');
    }
    setBusy(true);
    try {
      const r = await api.post('/equity/transfer', { ...txPayload(f), confirm: false });
      setPrepared(r.data.request);
      toast.success('Request prepared — full payload + signature below');
    } catch (e) { toast.error(e.response?.data?.detail || 'Prepare failed'); }
    finally { setBusy(false); }
  };

  const downloadDemoPack = async () => {
    let f = form;
    if (!formValid()) {
      f = withDemoDefaults(form);
      setForm(f);
      toast.info('Missing fields auto-filled with demo values');
    }
    setBusy(true);
    try {
      const r = await api.post('/equity/demo-pack', { ...txPayload(f), confirm: false }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `EzyTransfer_Equity_Jenga_DemoPack_${env}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Demo pack PDF downloaded');
    } catch (e) { toast.error('Demo pack failed'); }
    finally { setBusy(false); }
  };

  const openMintPicker = async () => {
    if (mintTxns) { setMintTxns(null); return; }
    if (form.source_rail === 'none') { toast.error('Select a source rail first'); return; }
    setMintLoading(true);
    try {
      const path = form.source_rail === 'cyclos' ? '/equity/cyclos-transactions?limit=15' : '/equity/mint-transactions?limit=15';
      const r = await api.get(path);
      setMintTxns(r.data.transactions || []);
    } catch (e) { toast.error(e.response?.data?.detail || `${form.source_rail} list failed`); }
    finally { setMintLoading(false); }
  };

  const pickMintTxn = (t) => {
    setForm((f) => ({
      ...f, source_rail_reference: t.transaction_number || '',
      _rail_details: { amount: t.amount, date: t.date, description: t.description, party: t.party },
    }));
    setMintTxns(null);
    toast.success(`${form.source_rail.toUpperCase()} txn ${t.transaction_number} linked as funding leg`);
  };

  const pollSettlement = async (s) => {
    setBusy(true);
    try {
      const r = await api.post(`/equity/settlements/${s.id}/poll`);
      toast.success(`${s.reference}: ${r.data.final_status || 'no final state yet'}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Poll failed'); }
    finally { setBusy(false); }
  };

  const downloadReceipt = async (s) => {
    try {
      const r = await api.get(`/equity/settlements/${s.id}/receipt`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `EzyTransfer_Equity_Receipt_${s.reference}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Receipt failed'); }
  };

  const execute = async () => {
    setBusy(true);
    try {
      const r = await api.post('/equity/transfer', { ...txPayload(), confirm: true });
      if (r.data.mode === 'executed') toast.success(`Sent — Jenga txn ${r.data.result.jenga_transaction_id || r.data.result.reference}`);
      else toast.error(`Jenga rejected: ${JSON.stringify(r.data.result.response).slice(0, 120)}`);
      setPrepared(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Execute failed'); }
    finally { setBusy(false); }
  };

  const st = health ? health[env] : null;

  return (
    <div className="space-y-6" data-testid="equity-layer">
      <Card className="p-6 border-red-200">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2"><Landmark className="w-5 h-5 text-red-700" /> EQUITY BANK — Jenga Settlement Layer</h3>
            <p className="text-xs text-slate-500">Mint / Cyclos → Equity via Jenga API v3 (IFT, EFT, RTGS, PesaLink, SWIFT) — RSA-SHA256 signed</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant={demoMode ? 'default' : 'outline'} size="sm" className={demoMode ? 'bg-red-700 hover:bg-red-800' : ''} onClick={() => setDemoMode(!demoMode)} data-testid="equity-demo-toggle-btn">
              <Presentation className="w-3.5 h-3.5 mr-1" /> Demo mode
            </Button>
            <select value={env} onChange={(e) => setEnv(e.target.value)} className="border rounded px-2 py-1 text-xs" data-testid="equity-env-select">
              <option value="sandbox">SANDBOX (UAT)</option>
              <option value="production">PRODUCTION</option>
            </select>
            <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="equity-reload-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        {st && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid="equity-config-badges">
            <Badge className={st.configured ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-800 border border-amber-200'} data-testid="equity-configured-badge">
              {st.configured ? `${env.toUpperCase()} CONFIGURED` : `${env.toUpperCase()} AWAITING CREDENTIALS`}
            </Badge>
            <Badge variant="outline" className="text-[10px]">merchant code {st.merchant_code_set ? '✓' : '—'}</Badge>
            <Badge variant="outline" className="text-[10px]">consumer secret {st.consumer_secret_set ? '✓' : '—'}</Badge>
            <Badge variant="outline" className="text-[10px]">api key {st.api_key_set ? '✓' : '—'}</Badge>
            <Badge variant="outline" className="text-[10px]">RSA keypair {st.rsa_keypair ? '✓' : '—'}</Badge>
            <span className="text-[10px] text-slate-400 self-center font-mono">{st.base_url}</span>
            <Button variant="outline" size="sm" onClick={testToken} disabled={busy || !st.configured} data-testid="equity-token-btn">
              <KeyRound className="w-3 h-3 mr-1" /> Test bearer token
            </Button>
          </div>
        )}
        {st && env === 'production' && !st.configured && (
          <p className="text-[11px] text-amber-700 mt-2" data-testid="equity-prod-checklist">
            Production go-live checklist: {!st.merchant_code_set && 'live merchant code · '}{!st.consumer_secret_set && 'live consumer secret · '}{!st.api_key_set && 'live API key · '}register our RSA public key under the production merchant — then USD send money goes live on api.finserve.africa
          </p>
        )}
        {tokenResult && (
          <p className="text-xs mt-2 font-mono" data-testid="equity-token-result">
            {tokenResult.ok ? `token: ${tokenResult.token_preview} (expires ${tokenResult.expires_in}s)` : `auth error: ${String(tokenResult.error).slice(0, 180)}`}
          </p>
        )}
        {health?.public_key && (
          <div className="mt-3 p-2 bg-slate-50 rounded border border-slate-200">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase text-slate-500 font-semibold">Our RSA public key — paste into Jenga portal (Generate Signature section)</span>
              <Button variant="ghost" size="sm" onClick={() => copyText(health.public_key)} data-testid="equity-pubkey-copy-btn"><Copy className="w-3 h-3" /></Button>
            </div>
            <pre className="text-[9px] font-mono text-slate-600 whitespace-pre-wrap leading-tight" data-testid="equity-pubkey-pre">{health.public_key}</pre>
          </div>
        )}
      </Card>

      {demoMode && <EquityDemoMode env={env} />}

      <Card className="p-6" data-testid="equity-signature-card">
        <h4 className="text-sm font-semibold flex items-center gap-2 mb-1"><FileSignature className="w-4 h-4 text-red-700" /> Generate Signature — live demo</h4>
        <p className="text-xs text-slate-500 mb-2">Per Jenga docs: concatenate the endpoint's fields in exact order (no separators), RSA-SHA256 sign with the private key, base64-encode. Field order differs per mode — IFT/Subsidiary: account+amount+currency+reference · RTGS/SWIFT: reference+date+srcAcct+destAcct+amount · PesaLink: amount+currency+reference+destName+srcAcct</p>
        <div className="flex gap-2">
          <Input value={sigFields} onChange={(e) => setSigFields(e.target.value)} className="font-mono text-xs" data-testid="equity-sig-input" />
          <Button onClick={genSignature} disabled={busy} className="bg-red-700 hover:bg-red-800" data-testid="equity-sig-btn">Sign</Button>
        </div>
        {sigResult && (
          <div className="mt-2 text-xs font-mono space-y-1" data-testid="equity-sig-result">
            <p className="text-slate-500">string: <span className="text-slate-800">{sigResult.concatenated_string}</span></p>
            <div className="flex items-start gap-1">
              <p className="text-slate-500 break-all">signature: <span className="text-emerald-700">{sigResult.signature}</span></p>
              <Button variant="ghost" size="sm" onClick={() => copyText(sigResult.signature)}><Copy className="w-3 h-3" /></Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-6" data-testid="equity-transfer-card">
        <h4 className="text-sm font-semibold mb-3">Send Money — all data fields</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div>
            <Label className="text-[10px]">Transfer mode</Label>
            <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="equity-mode-select">
              {MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>
          <div><Label className="text-[10px]">Source country</Label><Input value={form.source_country} onChange={(e) => setForm({ ...form, source_country: e.target.value })} data-testid="equity-src-country-input" /></div>
          <div><Label className="text-[10px]">Source name</Label><Input value={form.source_name} onChange={(e) => setForm({ ...form, source_name: e.target.value })} placeholder="EzyTransfer / Bullaale AS" data-testid="equity-src-name-input" /></div>
          <div><Label className="text-[10px]">Source account (Equity)</Label><Input value={form.source_account} onChange={(e) => setForm({ ...form, source_account: e.target.value })} data-testid="equity-src-account-input" /></div>
          <div><Label className="text-[10px]">Destination name</Label><Input value={form.dest_name} onChange={(e) => setForm({ ...form, dest_name: e.target.value })} data-testid="equity-dest-name-input" /></div>
          <div><Label className="text-[10px]">Destination account</Label><Input value={form.dest_account} onChange={(e) => setForm({ ...form, dest_account: e.target.value })} data-testid="equity-dest-account-input" /></div>
          <div><Label className="text-[10px]">Dest country</Label><Input value={form.dest_country} onChange={(e) => setForm({ ...form, dest_country: e.target.value })} data-testid="equity-dest-country-input" /></div>
          <div><Label className="text-[10px]">Bank code (EFT/PesaLink)</Label><Input value={form.dest_bank_code} onChange={(e) => setForm({ ...form, dest_bank_code: e.target.value })} data-testid="equity-dest-bankcode-input" /></div>
          <div><Label className="text-[10px]">Mobile (PesaLink mobile)</Label><Input value={form.dest_mobile} onChange={(e) => setForm({ ...form, dest_mobile: e.target.value })} data-testid="equity-dest-mobile-input" /></div>
          <div><Label className="text-[10px]">Bank BIC (SWIFT)</Label><Input value={form.dest_bank_bic} onChange={(e) => setForm({ ...form, dest_bank_bic: e.target.value })} data-testid="equity-dest-bic-input" /></div>
          <div><Label className="text-[10px]">Dest address (SWIFT)</Label><Input value={form.dest_address} onChange={(e) => setForm({ ...form, dest_address: e.target.value })} data-testid="equity-dest-address-input" /></div>
          <div><Label className="text-[10px]">Purpose code (RTGS)</Label><Input value={form.purpose_code} onChange={(e) => setForm({ ...form, purpose_code: e.target.value })} data-testid="equity-purpose-code-input" /></div>
          <div><Label className="text-[10px]">Amount</Label><Input type="number" value={form.amount} onChange={(e) => { setForm({ ...form, amount: e.target.value }); setPrepared(null); }} data-testid="equity-amount-input" /></div>
          <div><Label className="text-[10px]">Currency</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="equity-currency-input" /></div>
          <div><Label className="text-[10px]">Reference (auto if blank)</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="equity-reference-input" /></div>
          <div><Label className="text-[10px]">Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="equity-description-input" /></div>
          <div>
            <Label className="text-[10px]">Settled from (source rail)</Label>
            <select value={form.source_rail} onChange={(e) => setForm({ ...form, source_rail: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="equity-source-rail-select">
              <option value="mint">USD Mint</option>
              <option value="cyclos">Cyclos</option>
              <option value="none">None</option>
            </select>
          </div>
          <div>
            <Label className="text-[10px]">Mint/Cyclos txn ref</Label>
            <div className="flex gap-1">
              <Input value={form.source_rail_reference} onChange={(e) => setForm({ ...form, source_rail_reference: e.target.value })} placeholder="e.g. 0476075941" data-testid="equity-rail-ref-input" />
              <Button variant="outline" size="sm" className="shrink-0 px-2" onClick={openMintPicker} disabled={mintLoading} title="Pick a real Mint/Cyclos transaction" data-testid="equity-mint-pick-btn">
                {mintLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Pick'}
              </Button>
            </div>
          </div>
        </div>
        {form._rail_details && (
          <p className="text-[10px] text-emerald-700 mt-1 font-mono" data-testid="equity-rail-details">
            Funding leg: {form.source_rail.toUpperCase()} {form.source_rail_reference} · {Number(form._rail_details.amount).toFixed(2)} · {String(form._rail_details.date || '').slice(0, 10)} · {form._rail_details.description || form._rail_details.party || ''}
          </p>
        )}
        {mintTxns && (
          <div className="mt-2 border rounded max-h-48 overflow-y-auto" data-testid="equity-mint-txn-list">
            {mintTxns.length === 0 ? <p className="text-xs text-slate-500 p-2">No Mint transactions found.</p> : mintTxns.map((t) => (
              <button key={t.transaction_number} type="button" onClick={() => pickMintTxn(t)}
                className="w-full text-left px-2 py-1.5 text-[11px] font-mono hover:bg-red-50 border-b border-slate-100 flex justify-between gap-2"
                data-testid={`equity-mint-txn-use-${t.transaction_number}`}>
                <span>{t.transaction_number} · {String(t.date || '').slice(0, 10)} · {t.party || t.description || t.kind}</span>
                <span className={Number(t.amount) < 0 ? 'text-red-600' : 'text-emerald-700'}>{Number(t.amount).toFixed(2)}</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-2 mt-3 flex-wrap">
          {env === 'sandbox' && (
            <Button variant="secondary" onClick={loadTestData} disabled={busy} data-testid="equity-test-data-btn">
              Load UAT test data
            </Button>
          )}
          {env === 'production' && (
            <Button variant="secondary" onClick={loadProdConfig} disabled={busy} data-testid="equity-prod-config-btn">
              Load production USD config
            </Button>
          )}
          <Button variant="outline" onClick={prepare} disabled={busy} data-testid="equity-prepare-btn">
            <Eye className="w-4 h-4 mr-1" /> Prepare (show full request)
          </Button>
          <Button variant="outline" onClick={downloadDemoPack} disabled={busy} data-testid="equity-demo-pack-btn">
            <FileDown className="w-4 h-4 mr-1" /> Demo Pack (PDF)
          </Button>
          <Button className="bg-red-700 hover:bg-red-800" onClick={execute} disabled={busy || !prepared} data-testid="equity-execute-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Execute on {env.toUpperCase()}
          </Button>
        </div>
        {prepared && (
          <div className="mt-3 p-3 bg-slate-900 rounded text-[11px] font-mono text-slate-100 overflow-x-auto" data-testid="equity-prepared-view">
            <p className="text-emerald-400">POST {prepared.endpoint}</p>
            <p className="text-slate-400 mt-1">signature string: <span className="text-amber-300">{prepared.signature_string}</span></p>
            <p className="text-slate-400 break-all">signature (RSA-SHA256, base64): <span className="text-amber-300">{prepared.signature}</span></p>
            <pre className="mt-2 text-slate-200 whitespace-pre-wrap">{JSON.stringify(prepared.body, null, 2)}</pre>
          </div>
        )}
      </Card>

      <Card className="p-6" data-testid="equity-settlements-card">
        <h4 className="text-sm font-semibold mb-2">Equity settlements ledger</h4>
        {settlements.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid="equity-settlements-empty">No Equity settlements yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="equity-settlements-table">
              <thead className="text-[10px] uppercase text-slate-500 bg-slate-50"><tr><th className="text-left px-2 py-1">Ref</th><th className="text-left px-2 py-1">Env</th><th className="text-left px-2 py-1">Mode</th><th className="text-left px-2 py-1">Destination</th><th className="text-right px-2 py-1">Amount</th><th className="text-left px-2 py-1">Rail ref</th><th className="text-left px-2 py-1">Status</th><th className="text-left px-2 py-1">Date</th><th className="text-left px-2 py-1">Actions</th></tr></thead>
              <tbody>
                {settlements.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100" data-testid={`equity-settlement-row-${s.reference}`}>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{s.reference}</td>
                    <td className="px-2 py-1.5">{s.env}</td>
                    <td className="px-2 py-1.5">{s.mode}</td>
                    <td className="px-2 py-1.5">{s.destination?.name} · {s.destination?.account}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{Number(s.amount).toFixed(2)} {s.currency}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{s.source_rail}{s.source_rail_reference ? `:${s.source_rail_reference}` : ''}</td>
                    <td className="px-2 py-1.5">
                      <Badge className={`text-[10px] border ${(s.final_status === 'SUCCESS' || (s.ok && !s.final_status)) ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : s.final_status === 'AWAITING_CALLBACK' ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                        {s.final_status || s.jenga_status || (s.ok ? 'SENT' : `HTTP ${s.http_status}`)}
                      </Badge>
                    </td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{String(s.created_at || '').slice(0, 16)}</td>
                    <td className="px-2 py-1.5">
                      <div className="flex gap-1">
                        {s.ok && (
                          <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => pollSettlement(s)} disabled={busy} title="Query Jenga status now" data-testid={`equity-poll-btn-${s.reference}`}>
                            <RefreshCw className="w-3 h-3" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => downloadReceipt(s)} title="Download stamped receipt PDF" data-testid={`equity-receipt-btn-${s.reference}`}>
                          <FileDown className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default EquityLayer;
